import type { Lang } from '../types';

export interface SettingsPaymentMethodsText {
  typeBankTransfer: string;
  typeStripe: string;
  loadError: string;
  nameRequiredError: string;
  updateSuccess: string;
  createSuccess: string;
  saveError: string;
  deleteConfirm: string;
  deleteSuccess: string;
  deleteError: string;
  connectStartError: string;
  resetConfirm: string;
  resetError: string;
  stripeNotConfigured: string;
  stripeConnectedReady: string;
  stripeConnectedHint: string;
  stripeDetailsMissingWarning: string;
  stripeRedirecting: string;
  stripeFinishSetup: string;
  stripeResetting: string;
  stripeResetLink: string;
  stripeConnectCta: string;
  // Owner invitations — letting the apartment's owner onboard with their own bank account.
  inviteOwnerCta: string;
  inviteTitle: string;
  inviteDesc: string;
  inviteEmailLabel: string;
  inviteEmailPlaceholder: string;
  inviteSend: string;
  inviteSending: string;
  inviteError: string;
  inviteSentTo: string;
  inviteExpires: string;
  inviteStartedNote: string;
  inviteResend: string;
  inviteCopyLink: string;
  inviteCopied: string;
  inviteCancel: string;
  inviteCancelConfirm: string;
  pageTitle: string;
  pageDesc: string;
  addButton: string;
  limitWarning: string;
  chooseTypeTitle: string;
  bankTransferCardTitle: string;
  bankTransferCardDesc: string;
  stripeCardTitle: string;
  stripeCardDesc: string;
  cancel: string;
  editFormTitle: string;
  newFormTitlePrefix: string;
  fieldInternalName: string;
  placeholderInternalNameBank: string;
  placeholderInternalNameStripe: string;
  hintInternalName: string;
  fieldDisplayName: string;
  placeholderDisplayName: string;
  hintDisplayName: string;
  fieldAccountDetails: string;
  placeholderAccountDetails: string;
  stripeNewMethodNote: string;
  saving: string;
  saveChanges: string;
  add: string;
  loading: string;
  emptyState: string;
  editTooltip: string;
  deleteTooltip: string;
  forGuestLabel: string;
  displayNameUnset: string;
}

export const settingsPaymentMethodsText: Record<Lang, SettingsPaymentMethodsText> = {
  cs: {
    typeBankTransfer: 'Bankovní převod',
    typeStripe: 'Stripe (online platba)',
    loadError: 'Nepodařilo se načíst platební metody.',
    nameRequiredError: 'Interní název je povinný.',
    updateSuccess: 'Platební metoda byla aktualizována.',
    createSuccess: 'Platební metoda byla přidána.',
    saveError: 'Chyba při ukládání.',
    deleteConfirm: 'Opravdu smazat „{name}"?',
    deleteSuccess: 'Platební metoda byla smazána.',
    deleteError: 'Platební metodu se nepodařilo smazat.',
    connectStartError: 'Nepodařilo se zahájit připojení ke Stripe.',
    resetConfirm: 'Opravdu zrušit rozjednané propojení a začít znovu (např. po překlepu v e-mailu)? Nedokončený Stripe účet přestane být použitý.',
    resetError: 'Nepodařilo se zrušit propojení.',
    stripeNotConfigured: 'Stripe zatím není nastaven na straně serveru — kontaktujte podporu.',
    stripeConnectedReady: 'Připojeno a připraveno přijímat platby',
    stripeConnectedHint: 'Chcete-li tento účet použít pro výběr místního poplatku, přiřaďte ho v Detailu apartmánu → záložka „Místní poplatek".',
    stripeDetailsMissingWarning: 'Připojení začalo, ale Stripe ještě potřebuje doplnit údaje — dokud to nedokončíte, platby nelze přijímat.',
    stripeRedirecting: 'Přesměrování na Stripe…',
    stripeFinishSetup: 'Dokončit nastavení ve Stripe →',
    stripeResetting: 'Ruším…',
    stripeResetLink: 'Překlep v e-mailu? Začít znovu',
    stripeConnectCta: 'Propojit se Stripe →',
    inviteOwnerCta: 'Pozvat majitele',
    inviteTitle: 'Pozvat majitele k propojení',
    inviteDesc: 'Majitel dostane odkaz, na kterém propojí svůj vlastní Stripe účet — platby od hostů pak půjdou přímo na jeho bankovní účet. Účet v Guestbooku nepotřebuje.',
    inviteEmailLabel: 'E-mail majitele',
    inviteEmailPlaceholder: 'petr@example.com',
    inviteSend: 'Odeslat pozvánku',
    inviteSending: 'Odesílám…',
    inviteError: 'Pozvánku se nepodařilo odeslat.',
    inviteSentTo: 'Pozvánka odeslána na {email}',
    inviteExpires: 'platnost do {date}',
    inviteStartedNote: 'Majitel pozvánku otevřel, ale nastavení ve Stripe ještě nedokončil. Odkaz stále platí — může pokračovat tam, kde skončil.',
    inviteResend: 'Odeslat znovu',
    inviteCopyLink: 'Kopírovat odkaz',
    inviteCopied: 'Zkopírováno',
    inviteCancel: 'Zrušit pozvánku',
    inviteCancelConfirm: 'Opravdu zrušit pozvánku? Odkaz, který jste poslali, přestane platit.',
    pageTitle: 'Platební metody',
    pageDesc: 'Bankovní převody a Stripe účty, které pak přiřadíte k booking stránkám nebo k výběru místního poplatku.',
    addButton: '+ Přidat',
    limitWarning: 'Dosáhli jste limitu {limit} platebních metod. Pro navýšení limitu nás prosím kontaktujte na {email}.',
    chooseTypeTitle: 'Jaký typ platební metody chcete přidat?',
    bankTransferCardTitle: '🏦 Bankovní převod',
    bankTransferCardDesc: 'Číslo účtu a platební instrukce, které uvidí host.',
    stripeCardTitle: '💳 Stripe (online platba)',
    stripeCardDesc: 'Vlastní Stripe účet — propojení nastavíte hned po vytvoření.',
    cancel: 'Zrušit',
    editFormTitle: 'Upravit platební metodu',
    newFormTitlePrefix: 'Nová platební metoda —',
    fieldInternalName: 'Interní název',
    placeholderInternalNameBank: 'např. Bankovní převod CZK',
    placeholderInternalNameStripe: 'např. Stripe — hlavní účet',
    hintInternalName: 'Viditelný pouze pro vás v administraci.',
    fieldDisplayName: 'Zobrazovaný název pro hosta',
    placeholderDisplayName: 'např. Platba převodem',
    hintDisplayName: 'Tento název uvidí host na booking stránce. Pokud nevyplníte, použije se interní název.',
    fieldAccountDetails: 'Číslo účtu / platební instrukce',
    placeholderAccountDetails: 'Číslo účtu: 1234567890/0800\nVS: číslo rezervace',
    stripeNewMethodNote: 'Po vytvoření se u této metody objeví tlačítko „Propojit se Stripe" — samotné propojení proběhne na Stripe.',
    saving: 'Ukládám…',
    saveChanges: 'Uložit změny',
    add: 'Přidat',
    loading: 'Načítám…',
    emptyState: 'Zatím nemáte žádné platební metody. Přidejte první kliknutím na tlačítko výše.',
    editTooltip: 'Upravit',
    deleteTooltip: 'Smazat',
    forGuestLabel: 'Pro hosta:',
    displayNameUnset: 'Zobrazovaný název nevyplněn',
  },
  en: {
    typeBankTransfer: 'Bank transfer',
    typeStripe: 'Stripe (online payment)',
    loadError: 'Failed to load payment methods.',
    nameRequiredError: 'Internal name is required.',
    updateSuccess: 'Payment method was updated.',
    createSuccess: 'Payment method was added.',
    saveError: 'Error while saving.',
    deleteConfirm: 'Really delete "{name}"?',
    deleteSuccess: 'Payment method was deleted.',
    deleteError: 'Failed to delete the payment method.',
    connectStartError: 'Failed to start the Stripe connection.',
    resetConfirm: 'Really cancel the in-progress connection and start over (e.g. after a typo in the email)? The unfinished Stripe account will stop being used.',
    resetError: 'Failed to cancel the connection.',
    stripeNotConfigured: 'Stripe isn\'t configured on the server yet — please contact support.',
    stripeConnectedReady: 'Connected and ready to accept payments',
    stripeConnectedHint: 'To use this account for city-tax collection, assign it in the apartment\'s Detail → "City Tax" tab.',
    stripeDetailsMissingWarning: 'Connection started, but Stripe still needs more details — payments can\'t be accepted until you finish it.',
    stripeRedirecting: 'Redirecting to Stripe…',
    stripeFinishSetup: 'Finish setup on Stripe →',
    stripeResetting: 'Cancelling…',
    stripeResetLink: 'Typo in the email? Start over',
    stripeConnectCta: 'Connect with Stripe →',
    inviteOwnerCta: 'Invite owner',
    inviteTitle: 'Invite the owner to connect',
    inviteDesc: 'The owner gets a link where they connect their own Stripe account — guest payments then go straight to their bank account. They don\'t need a Guestbook account.',
    inviteEmailLabel: 'Owner\'s email',
    inviteEmailPlaceholder: 'petr@example.com',
    inviteSend: 'Send invitation',
    inviteSending: 'Sending…',
    inviteError: 'The invitation could not be sent.',
    inviteSentTo: 'Invitation sent to {email}',
    inviteExpires: 'expires {date}',
    inviteStartedNote: 'The owner opened the invitation but hasn\'t finished Stripe\'s setup yet. The link still works — they\'ll pick up where they left off.',
    inviteResend: 'Resend',
    inviteCopyLink: 'Copy link',
    inviteCopied: 'Copied',
    inviteCancel: 'Cancel invitation',
    inviteCancelConfirm: 'Cancel this invitation? The link you sent will stop working.',
    pageTitle: 'Payment Methods',
    pageDesc: 'Bank transfers and Stripe accounts you can then assign to booking sites or to city-tax collection.',
    addButton: '+ Add',
    limitWarning: 'You\'ve reached the limit of {limit} payment methods. Contact us at {email} to raise the limit.',
    chooseTypeTitle: 'What type of payment method do you want to add?',
    bankTransferCardTitle: '🏦 Bank transfer',
    bankTransferCardDesc: 'Account number and payment instructions the guest will see.',
    stripeCardTitle: '💳 Stripe (online payment)',
    stripeCardDesc: 'Your own Stripe account — you\'ll connect it right after creating this.',
    cancel: 'Cancel',
    editFormTitle: 'Edit payment method',
    newFormTitlePrefix: 'New payment method —',
    fieldInternalName: 'Internal name',
    placeholderInternalNameBank: 'e.g. Bank transfer CZK',
    placeholderInternalNameStripe: 'e.g. Stripe — main account',
    hintInternalName: 'Only visible to you in the admin panel.',
    fieldDisplayName: 'Display name for the guest',
    placeholderDisplayName: 'e.g. Pay by transfer',
    hintDisplayName: 'The guest sees this name on the booking page. If left blank, the internal name is used instead.',
    fieldAccountDetails: 'Account number / payment instructions',
    placeholderAccountDetails: 'Account number: 1234567890/0800\nVS: reservation number',
    stripeNewMethodNote: 'Once created, a "Connect with Stripe" button will appear on this method — the actual connection happens on Stripe.',
    saving: 'Saving…',
    saveChanges: 'Save changes',
    add: 'Add',
    loading: 'Loading…',
    emptyState: 'You don\'t have any payment methods yet. Add your first one using the button above.',
    editTooltip: 'Edit',
    deleteTooltip: 'Delete',
    forGuestLabel: 'For the guest:',
    displayNameUnset: 'No display name set',
  },
  vi: {
    typeBankTransfer: 'Chuyển khoản ngân hàng',
    typeStripe: 'Stripe (thanh toán trực tuyến)',
    loadError: 'Không thể tải phương thức thanh toán.',
    nameRequiredError: 'Tên nội bộ là bắt buộc.',
    updateSuccess: 'Phương thức thanh toán đã được cập nhật.',
    createSuccess: 'Phương thức thanh toán đã được thêm.',
    saveError: 'Lỗi khi lưu.',
    deleteConfirm: 'Bạn có chắc muốn xóa „{name}"?',
    deleteSuccess: 'Phương thức thanh toán đã được xóa.',
    deleteError: 'Không thể xóa phương thức thanh toán.',
    connectStartError: 'Không thể bắt đầu kết nối với Stripe.',
    resetConfirm: 'Bạn có chắc muốn hủy kết nối đang thực hiện và bắt đầu lại (ví dụ sau khi gõ sai email)? Tài khoản Stripe chưa hoàn tất sẽ không còn được sử dụng.',
    resetError: 'Không thể hủy kết nối.',
    stripeNotConfigured: 'Stripe chưa được cấu hình trên máy chủ — vui lòng liên hệ hỗ trợ.',
    stripeConnectedReady: 'Đã kết nối và sẵn sàng nhận thanh toán',
    stripeConnectedHint: 'Để sử dụng tài khoản này cho việc thu phí lưu trú, hãy gán nó trong Chi tiết căn hộ → tab „Phí lưu trú".',
    stripeDetailsMissingWarning: 'Kết nối đã bắt đầu, nhưng Stripe vẫn cần bổ sung thông tin — chưa thể nhận thanh toán cho đến khi bạn hoàn tất.',
    stripeRedirecting: 'Đang chuyển hướng đến Stripe…',
    stripeFinishSetup: 'Hoàn tất thiết lập trên Stripe →',
    stripeResetting: 'Đang hủy…',
    stripeResetLink: 'Gõ sai email? Bắt đầu lại',
    stripeConnectCta: 'Kết nối với Stripe →',
    inviteOwnerCta: 'Mời chủ nhà',
    inviteTitle: 'Mời chủ nhà kết nối',
    inviteDesc: 'Chủ nhà sẽ nhận được một liên kết để kết nối tài khoản Stripe của chính họ — tiền khách trả sẽ vào thẳng tài khoản ngân hàng của họ. Họ không cần tài khoản Guestbook.',
    inviteEmailLabel: 'Email của chủ nhà',
    inviteEmailPlaceholder: 'petr@example.com',
    inviteSend: 'Gửi lời mời',
    inviteSending: 'Đang gửi…',
    inviteError: 'Không gửi được lời mời.',
    inviteSentTo: 'Đã gửi lời mời tới {email}',
    inviteExpires: 'hết hạn {date}',
    inviteStartedNote: 'Chủ nhà đã mở lời mời nhưng chưa hoàn tất thiết lập trên Stripe. Liên kết vẫn còn hiệu lực — họ có thể tiếp tục từ chỗ đang dở.',
    inviteResend: 'Gửi lại',
    inviteCopyLink: 'Sao chép liên kết',
    inviteCopied: 'Đã sao chép',
    inviteCancel: 'Huỷ lời mời',
    inviteCancelConfirm: 'Huỷ lời mời này? Liên kết bạn đã gửi sẽ ngừng hoạt động.',
    pageTitle: 'Phương thức thanh toán',
    pageDesc: 'Chuyển khoản ngân hàng và tài khoản Stripe mà bạn có thể gán cho trang đặt phòng hoặc thu phí lưu trú.',
    addButton: '+ Thêm',
    limitWarning: 'Bạn đã đạt giới hạn {limit} phương thức thanh toán. Vui lòng liên hệ {email} để tăng giới hạn.',
    chooseTypeTitle: 'Bạn muốn thêm loại phương thức thanh toán nào?',
    bankTransferCardTitle: '🏦 Chuyển khoản ngân hàng',
    bankTransferCardDesc: 'Số tài khoản và hướng dẫn thanh toán mà khách sẽ nhìn thấy.',
    stripeCardTitle: '💳 Stripe (thanh toán trực tuyến)',
    stripeCardDesc: 'Tài khoản Stripe của riêng bạn — bạn sẽ kết nối ngay sau khi tạo.',
    cancel: 'Hủy',
    editFormTitle: 'Chỉnh sửa phương thức thanh toán',
    newFormTitlePrefix: 'Phương thức thanh toán mới —',
    fieldInternalName: 'Tên nội bộ',
    placeholderInternalNameBank: 'vd: Chuyển khoản CZK',
    placeholderInternalNameStripe: 'vd: Stripe — tài khoản chính',
    hintInternalName: 'Chỉ hiển thị cho bạn trong trang quản trị.',
    fieldDisplayName: 'Tên hiển thị cho khách',
    placeholderDisplayName: 'vd: Thanh toán chuyển khoản',
    hintDisplayName: 'Khách sẽ thấy tên này trên trang đặt phòng. Nếu để trống, tên nội bộ sẽ được sử dụng.',
    fieldAccountDetails: 'Số tài khoản / hướng dẫn thanh toán',
    placeholderAccountDetails: 'Số tài khoản: 1234567890/0800\nVS: số đặt phòng',
    stripeNewMethodNote: 'Sau khi tạo, nút „Kết nối với Stripe" sẽ xuất hiện ở phương thức này — việc kết nối thực sự diễn ra trên Stripe.',
    saving: 'Đang lưu…',
    saveChanges: 'Lưu thay đổi',
    add: 'Thêm',
    loading: 'Đang tải…',
    emptyState: 'Bạn chưa có phương thức thanh toán nào. Thêm phương thức đầu tiên bằng nút phía trên.',
    editTooltip: 'Chỉnh sửa',
    deleteTooltip: 'Xóa',
    forGuestLabel: 'Cho khách:',
    displayNameUnset: 'Chưa đặt tên hiển thị',
  },
  uk: {
    typeBankTransfer: 'Банківський переказ',
    typeStripe: 'Stripe (онлайн-оплата)',
    loadError: 'Не вдалося завантажити способи оплати.',
    nameRequiredError: 'Внутрішня назва є обов\'язковою.',
    updateSuccess: 'Спосіб оплати оновлено.',
    createSuccess: 'Спосіб оплати додано.',
    saveError: 'Помилка під час збереження.',
    deleteConfirm: 'Дійсно видалити «{name}»?',
    deleteSuccess: 'Спосіб оплати видалено.',
    deleteError: 'Не вдалося видалити спосіб оплати.',
    connectStartError: 'Не вдалося розпочати підключення до Stripe.',
    resetConfirm: 'Дійсно скасувати розпочате підключення і почати заново (наприклад, після помилки в email)? Незавершений акаунт Stripe більше не використовуватиметься.',
    resetError: 'Не вдалося скасувати підключення.',
    stripeNotConfigured: 'Stripe ще не налаштований на стороні сервера — зверніться до підтримки.',
    stripeConnectedReady: 'Підключено та готово приймати платежі',
    stripeConnectedHint: 'Щоб використати цей акаунт для збору місцевого податку, призначте його в Деталях апартаменту → вкладка «Місцевий податок».',
    stripeDetailsMissingWarning: 'Підключення розпочато, але Stripe ще потребує додаткових даних — доки ви не завершите, платежі приймати не можна.',
    stripeRedirecting: 'Перенаправлення до Stripe…',
    stripeFinishSetup: 'Завершити налаштування на Stripe →',
    stripeResetting: 'Скасування…',
    stripeResetLink: 'Помилка в email? Почати заново',
    stripeConnectCta: 'Підключити Stripe →',
    inviteOwnerCta: 'Запросити власника',
    inviteTitle: 'Запросити власника до підключення',
    inviteDesc: 'Власник отримає посилання, за яким підключить свій власний рахунок Stripe — платежі від гостей надходитимуть просто на його банківський рахунок. Обліковий запис у Guestbook йому не потрібен.',
    inviteEmailLabel: 'Email власника',
    inviteEmailPlaceholder: 'petr@example.com',
    inviteSend: 'Надіслати запрошення',
    inviteSending: 'Надсилаю…',
    inviteError: 'Не вдалося надіслати запрошення.',
    inviteSentTo: 'Запрошення надіслано на {email}',
    inviteExpires: 'діє до {date}',
    inviteStartedNote: 'Власник відкрив запрошення, але ще не завершив налаштування у Stripe. Посилання досі дійсне — він продовжить з того місця, де зупинився.',
    inviteResend: 'Надіслати знову',
    inviteCopyLink: 'Скопіювати посилання',
    inviteCopied: 'Скопійовано',
    inviteCancel: 'Скасувати запрошення',
    inviteCancelConfirm: 'Скасувати це запрошення? Посилання, яке ви надіслали, перестане працювати.',
    pageTitle: 'Способи оплати',
    pageDesc: 'Банківські перекази та акаунти Stripe, які потім можна призначити для сторінок бронювання або збору місцевого податку.',
    addButton: '+ Додати',
    limitWarning: 'Ви досягли ліміту {limit} способів оплати. Зв\'яжіться з нами на {email}, щоб підвищити ліміт.',
    chooseTypeTitle: 'Який тип способу оплати ви хочете додати?',
    bankTransferCardTitle: '🏦 Банківський переказ',
    bankTransferCardDesc: 'Номер рахунку та платіжні інструкції, які побачить гість.',
    stripeCardTitle: '💳 Stripe (онлайн-оплата)',
    stripeCardDesc: 'Власний акаунт Stripe — підключення налаштуєте одразу після створення.',
    cancel: 'Скасувати',
    editFormTitle: 'Редагувати спосіб оплати',
    newFormTitlePrefix: 'Новий спосіб оплати —',
    fieldInternalName: 'Внутрішня назва',
    placeholderInternalNameBank: 'напр. Банківський переказ CZK',
    placeholderInternalNameStripe: 'напр. Stripe — основний акаунт',
    hintInternalName: 'Видно лише вам в адміністрації.',
    fieldDisplayName: 'Назва для гостя',
    placeholderDisplayName: 'напр. Оплата переказом',
    hintDisplayName: 'Гість побачить цю назву на сторінці бронювання. Якщо не заповнено, використовується внутрішня назва.',
    fieldAccountDetails: 'Номер рахунку / платіжні інструкції',
    placeholderAccountDetails: 'Номер рахунку: 1234567890/0800\nVS: номер бронювання',
    stripeNewMethodNote: 'Після створення на цьому способі з\'явиться кнопка «Підключити Stripe» — саме підключення відбувається на Stripe.',
    saving: 'Збереження…',
    saveChanges: 'Зберегти зміни',
    add: 'Додати',
    loading: 'Завантаження…',
    emptyState: 'У вас ще немає способів оплати. Додайте перший за допомогою кнопки вище.',
    editTooltip: 'Редагувати',
    deleteTooltip: 'Видалити',
    forGuestLabel: 'Для гостя:',
    displayNameUnset: 'Назву для гостя не вказано',
  },
};
